import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
const PAGE_SIZE = 50;
export default function FormSubmissionsTab({ formId, formDef, smtpReady }) {
    const [rows, setRows] = useState(null);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [filter, setFilter] = useState('all');
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [selected, setSelected] = useState(null);
    const [toast, setToast] = useState(null);
    const refresh = useCallback(async () => {
        setError(null);
        try {
            const r = await api.listSubmissions(formId, { limit: PAGE_SIZE, offset });
            setRows(r.submissions);
            setTotal(r.total);
        }
        catch (e) {
            setError(e?.message ?? 'Failed to load');
        }
    }, [formId, offset]);
    useEffect(() => { refresh(); }, [refresh]);
    const counts = useMemo(() => {
        const list = rows ?? [];
        return {
            all: list.length,
            ok: list.filter((r) => r.webhook_status === 'ok' && !r.is_test).length,
            fail: list.filter((r) => r.webhook_status === 'fail' || r.email_status === 'fail').length,
            test: list.filter((r) => !!r.is_test).length,
        };
    }, [rows]);
    const filtered = useMemo(() => {
        if (!rows)
            return null;
        return rows.filter((r) => {
            if (filter === 'ok')
                return r.webhook_status === 'ok' && !r.is_test;
            if (filter === 'fail')
                return r.webhook_status === 'fail' || r.email_status === 'fail';
            if (filter === 'test')
                return !!r.is_test;
            return true;
        });
    }, [rows, filter]);
    async function deleteOne(id) {
        if (!window.confirm('Delete this submission permanently?'))
            return;
        try {
            await api.deleteSubmission(formId, id);
            await refresh();
            if (selected?.id === id)
                setSelected(null);
        }
        catch (e) {
            alert(e?.message ?? 'Delete failed');
        }
    }
    async function retryWebhook(id) {
        setBusyId(id);
        setToast(null);
        try {
            const r = await api.retrySubmissionWebhook(formId, id);
            setToast(r.result.ok ? 'Webhook re-fired ✓' : `Webhook failed (HTTP ${r.result.http})`);
            await refresh();
        }
        catch (e) {
            setToast(e?.message ?? 'Retry failed');
        }
        finally {
            setBusyId(null);
            setTimeout(() => setToast(null), 2500);
        }
    }
    async function resendEmail(id) {
        setBusyId(id);
        setToast(null);
        try {
            const r = await api.resendSubmissionEmail(formId, id);
            setToast(r.result.ok
                ? `Email resent (${r.result.sent}/${r.result.total}) ✓`
                : `Email send failed: ${r.result.error || r.result.status}`);
            await refresh();
        }
        catch (e) {
            setToast(e?.message ?? 'Resend failed');
        }
        finally {
            setBusyId(null);
            setTimeout(() => setToast(null), 2500);
        }
    }
    const labelOf = (fid) => formDef.fields.find((f) => f.id === fid)?.label || fid;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
    return (_jsxs("div", { className: "form-tab-pane form-submissions", children: [_jsxs("div", { className: "submissions-toolbar", children: [_jsx("div", { className: "filter-pills", children: ['all', 'ok', 'fail', 'test'].map((id) => (_jsxs("button", { type: "button", className: filter === id ? 'pill active' : 'pill', onClick: () => setFilter(id), children: [labelForFilter(id), _jsx("span", { className: "pill-count", children: counts[id] })] }, id))) }), _jsxs("div", { className: "actions", children: [_jsx("a", { className: "ghost", href: api.submissionsCsvUrl(formId), children: "\u2913 Export CSV" }), _jsx("button", { className: "ghost", onClick: refresh, children: "\u21BB Refresh" })] })] }), error && _jsx("div", { className: "err small pad-y", children: error }), toast && _jsx("div", { className: "callout ok-callout", children: toast }), _jsxs("div", { className: "submissions-layout", children: [_jsxs("div", { className: "submissions-table-wrap", children: [!filtered && _jsx("div", { className: "muted pad", children: "Loading\u2026" }), filtered && filtered.length === 0 && (_jsx("div", { className: "callout muted-callout", children: _jsx("strong", { children: "No submissions match this filter." }) })), filtered && filtered.length > 0 && (_jsxs("table", { className: "data-table compact", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "When" }), _jsx("th", { children: "Webhook" }), _jsx("th", { children: "Email" }), _jsx("th", { children: "IP" }), _jsx("th", { children: "Preview" })] }) }), _jsx("tbody", { children: filtered.map((s) => (_jsxs("tr", { onClick: () => setSelected(s), className: (selected?.id === s.id ? 'selected ' : '') + (s.is_test ? 'test-row' : ''), children: [_jsxs("td", { className: "small", children: [s.is_test ? _jsx("span", { className: "tag warn-tag", style: { marginRight: 6 }, children: "TEST" }) : null, s.created_at] }), _jsx("td", { children: _jsx(StatusBadge, { status: s.webhook_status, error: s.webhook_error }) }), _jsx("td", { children: _jsx(StatusBadge, { status: s.email_status, error: s.email_error }) }), _jsx("td", { className: "small", children: s.ip || '—' }), _jsx("td", { className: "muted small payload-preview", children: Object.entries(s.payload).slice(0, 3).map(([k, v]) => (_jsxs("span", { children: [_jsxs("strong", { children: [labelOf(k), ":"] }), " ", previewVal(v), " "] }, k))) })] }, s.id))) })] })), filtered && total > PAGE_SIZE && (_jsxs("div", { className: "pagination", children: [_jsx("button", { className: "ghost small", onClick: () => setOffset(Math.max(0, offset - PAGE_SIZE)), disabled: offset === 0, children: "\u2039 Prev" }), _jsxs("span", { className: "muted small", children: ["Page ", currentPage, " of ", totalPages, " \u00B7 ", total, " total"] }), _jsx("button", { className: "ghost small", onClick: () => setOffset(offset + PAGE_SIZE), disabled: offset + PAGE_SIZE >= total, children: "Next \u203A" })] }))] }), selected && (_jsxs("aside", { className: "submission-detail", children: [_jsxs("div", { className: "submission-detail-head", children: [_jsxs("strong", { children: ["Submission #", selected.id] }), _jsx("button", { className: "ghost small", onClick: () => setSelected(null), children: "\u2715" })] }), _jsxs("div", { className: "submission-detail-body", children: [_jsx(SubmissionTimeline, { sub: selected, smtpReady: smtpReady, hasNotifyEmails: (formDef.notify_emails ?? []).length > 0, busy: busyId === selected.id, onRetryWebhook: () => retryWebhook(selected.id), onResendEmail: () => resendEmail(selected.id) }), _jsx("h4", { children: "Submitted values" }), _jsx("table", { className: "kv-table", children: _jsx("tbody", { children: Object.entries(selected.payload).map(([k, v]) => (_jsxs("tr", { children: [_jsxs("th", { children: [labelOf(k), _jsx("div", { className: "muted small", children: _jsx("code", { children: k }) })] }), _jsx("td", { children: renderVal(v) })] }, k))) }) }), _jsx("h4", { children: "Request" }), _jsxs("dl", { className: "kv", children: [_jsx("dt", { children: "When" }), _jsx("dd", { children: selected.created_at }), _jsx("dt", { children: "IP" }), _jsx("dd", { children: selected.ip || '—' }), selected.referrer && (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Referrer" }), _jsx("dd", { className: "break", children: selected.referrer })] })), selected.is_test ? (_jsxs(_Fragment, { children: [_jsx("dt", { children: "Test?" }), _jsx("dd", { children: _jsx("span", { className: "tag warn-tag", children: "Test submission" }) })] })) : null] }), _jsxs("details", { children: [_jsx("summary", { className: "muted small", children: "User agent" }), _jsx("p", { className: "muted small break", children: selected.user_agent || '—' })] })] }), _jsx("div", { className: "submission-detail-foot", children: _jsx("button", { className: "ghost small danger", onClick: () => deleteOne(selected.id), children: "Delete submission" }) })] }))] })] }));
}
function SubmissionTimeline({ sub, smtpReady, hasNotifyEmails, busy, onRetryWebhook, onResendEmail }) {
    return (_jsxs("ol", { className: "status-timeline", children: [_jsxs("li", { className: "step ok", children: [_jsx("span", { className: "step-dot", children: "\u2713" }), _jsxs("div", { className: "step-body", children: [_jsx("div", { className: "step-title", children: "Persisted" }), _jsxs("div", { className: "muted small", children: ["Submission stored as #", sub.id, " at ", sub.created_at] })] })] }), _jsxs("li", { className: `step ${webhookStepClass(sub.webhook_status)}`, children: [_jsx("span", { className: "step-dot", children: stepIcon(sub.webhook_status) }), _jsxs("div", { className: "step-body", children: [_jsxs("div", { className: "step-title", children: ["Webhook ", _jsx(StatusBadge, { status: sub.webhook_status })] }), sub.webhook_error && _jsx("div", { className: "muted small err", children: sub.webhook_error }), _jsx("div", { className: "step-actions", children: _jsx("button", { className: "ghost small", onClick: onRetryWebhook, disabled: busy, children: "\u21BB Retry webhook" }) })] })] }), _jsxs("li", { className: `step ${emailStepClass(sub.email_status, hasNotifyEmails, smtpReady)}`, children: [_jsx("span", { className: "step-dot", children: stepIcon(sub.email_status) }), _jsxs("div", { className: "step-body", children: [_jsxs("div", { className: "step-title", children: ["Email notifications ", _jsx(StatusBadge, { status: sub.email_status || (hasNotifyEmails ? 'pending' : 'no_recipients') })] }), sub.email_error && _jsx("div", { className: "muted small err", children: sub.email_error }), !smtpReady && (sub.email_status === '' || sub.email_status === 'unconfigured') && (_jsxs("div", { className: "muted small", children: ["SMTP not configured \u00B7 ", _jsx(Link, { to: "/settings?tab=email", children: "Set up \u2192" })] })), !hasNotifyEmails && (_jsxs("div", { className: "muted small", children: ["No ", _jsx("code", { children: "notify_emails" }), " configured for this form."] })), _jsx("div", { className: "step-actions", children: _jsx("button", { className: "ghost small", onClick: onResendEmail, disabled: busy || !hasNotifyEmails, title: !hasNotifyEmails ? 'No notify_emails configured' : 'Re-send the notify-email summary', children: "\uD83D\uDCE7 Resend email" }) })] })] })] }));
}
function webhookStepClass(s) {
    if (s === 'ok')
        return 'ok';
    if (s === 'fail')
        return 'err';
    if (s === 'no_webhook')
        return 'muted';
    return 'pending';
}
function emailStepClass(s, hasRecipients, smtpReady) {
    if (s === 'ok')
        return 'ok';
    if (s === 'fail')
        return 'err';
    if (s === 'unconfigured' || !smtpReady)
        return 'warn';
    if (!hasRecipients)
        return 'muted';
    return 'pending';
}
function stepIcon(s) {
    if (s === 'ok')
        return '✓';
    if (s === 'fail')
        return '✗';
    if (s === 'unconfigured')
        return '⚠';
    if (s === '' || s === 'pending')
        return '·';
    return '—';
}
function labelForFilter(id) {
    return { all: 'All', ok: 'Delivered', fail: 'Failures', test: 'Test runs' }[id];
}
function previewVal(v) {
    if (Array.isArray(v))
        return v.join(', ');
    if (v == null)
        return '—';
    const s = String(v);
    return s.length > 36 ? s.slice(0, 36) + '…' : s;
}
function renderVal(v) {
    if (Array.isArray(v))
        return v.length ? v.join(', ') : _jsx("em", { className: "muted", children: "\u2014" });
    if (v == null || v === '')
        return _jsx("em", { className: "muted", children: "\u2014" });
    return String(v);
}
function StatusBadge({ status, error }) {
    const cls = status === 'ok' ? 'tag ok-tag'
        : status === 'fail' ? 'tag err-tag'
            : status === 'pending' ? 'tag muted-tag'
                : status === 'no_webhook' ? 'tag muted-tag'
                    : status === 'no_recipients' ? 'tag muted-tag'
                        : status === 'unconfigured' ? 'tag warn-tag'
                            : 'tag muted-tag';
    const label = status || 'pending';
    return _jsx("span", { className: cls, title: error || '', children: label });
}
